import { Scoring } from "./Scoring";
import { BoardModel } from "./model/BoardModel";
import { rotateBlock, moveSideways, stepDown, zoomDown } from "./model/tempHomeForActions";
import { removeRows } from "./removeRows";
import { LayoutSketch } from "./common";
import { Shuffler } from "./Shuffler";
import { BlockModel } from "./model/BlockModel";
import { BlockViewModel } from "./Board";

type BlockTemplate = [number, LayoutSketch];

const blockTemplates: BlockTemplate[] = [
  [0, [" ##", "## "]],
  [2 * 36, [" # ", "###"]],
  [4 * 36, ["##", "##"]],
  [6 * 36, ["#  ", "###"]],
  [8 * 36, ["####"]],
  [9 * 36, ["  #", "###"]],
  [7 * 36, ["## ", " ##"]],
];

const blockTemplatesShuffler = new Shuffler(blockTemplates);

function buildBlock([hue, layoutSketch]: BlockTemplate) {
  return {
    hue,
    x: 4,
    y: 0,
    block: BlockModel.buildFromSketch(layoutSketch)
  };
}

function buildInitialBlock() {
  return buildBlock(blockTemplatesShuffler.pop());
}

interface GameState {
  gameOver: boolean;
  paused: boolean;
  boardModel: BoardModel;
  currentBlock: BlockViewModel | undefined;
  scoring: Scoring;
  placedBlocks: BlockViewModel[];
}

class GameStateManager {
  public state: GameState;
  private stateChangeCallback: () => void;

  constructor(state: GameState) {
    this.state = state;
    this.stateChangeCallback = () => {};
  }

  onStateChange(cb: () => void) {
    this.stateChangeCallback = cb;
  }

  forceChanged() {
    this.stateChangeCallback();
  }

  setState(state: Partial<GameState>) {
    this.state = {...this.state, ...state};
    this.stateChangeCallback();
  }
  
  shouldLoopRun(): boolean {
    return !this.state.paused && !this.state.gameOver;
  }

  nextBlockViewModel(): BlockViewModel {
    return buildBlock(blockTemplatesShuffler.peek());
  }

  togglePause() {
    this.setState({paused: !this.state.paused});
  }

  zoomDown(): boolean {
    if (this.state.currentBlock) {
      zoomDown(this.state.boardModel, this.state.currentBlock);
      this.stateChangeCallback();
      return true;
    } else {
      return false;
    }
  }

  rotateBlock(): void {
    let newBlockViewModel;
    if (this.state.currentBlock) {
      if ((newBlockViewModel = rotateBlock(this.state.boardModel, this.state.currentBlock))) {
        this.setState({currentBlock: newBlockViewModel});
        this.forceChanged();
      }
    }
  }

  moveSideways(direction: number): boolean {
    if (this.state.currentBlock && moveSideways(this.state.boardModel, this.state.currentBlock, direction)) {
      this.forceChanged();
      return true;
    } else {
      return false;
    }
  }

  maybeInsertNewBlock(): void {
    if (!this.state.currentBlock && this.shouldLoopRun()) {
      const newBlock = buildInitialBlock();
      if (this.state.boardModel.doesBlockFit(newBlock.x, newBlock.y, newBlock.block)) {
        this.setState({currentBlock: newBlock});
      } else {
        this.setState({gameOver: true});
      }
    }
  }

  stepDown(): {removedRows: boolean} {
    let removedRows = false;
    const currentBlockLocked = stepDown(this.state.boardModel, this.state.currentBlock!);
    if (currentBlockLocked) {
      this.setState({placedBlocks: this.state.placedBlocks.concat([this.state.currentBlock!]) })

      const completedRows = this.state.boardModel.completeRows();
      console.log(`completedRows ${completedRows}`);
      this.state.boardModel.removeRows(completedRows);
      this.setState({placedBlocks: removeRows(this.state.placedBlocks, completedRows)})

      this.state.scoring.addClearedRows(completedRows.length);
      console.log(`cleared rows: ${this.state.scoring.getNumberOfClearedRows()}`);

      if (completedRows.length > 0) {
        removedRows = true;
      }
      this.setState({currentBlock: undefined});
    }
    this.forceChanged();
    return {removedRows};
  }
}

export const initialGameState: () => GameStateManager = () => new GameStateManager({
  gameOver: false,
  paused: false,
  boardModel: BoardModel.buildEmptyBoard(10, 17),
  currentBlock: undefined,
  scoring: new Scoring(),
  placedBlocks: []
});
